import { useMatch, useParams } from 'react-router';

import { useQuery } from '@tanstack/react-query';
import type { ExtractAtomValue } from 'jotai';
import { useAtomValue } from 'jotai';
import { parseAsBoolean, useQueryState } from 'nuqs';

import {
  ContentStep,
  getPlanAndAssignmentsOptions,
  UserPlans,
} from '@cfa/pathway-fe-shared/xp';

import useAuth from '@/hooks/useAuth';

import { isValidPlanId } from '@/utils/isValidPlanId';
import { itemByLanguage } from '@/utils/language';

import Constants from '@/constants';

import { trainTogetherModeAtom } from '@/atoms/trainTogetherMode';
import { getPlanStepResourceReference } from '@/routes/TrainingPlans/getPlanStepResourceReference';
import { useIsAssignedToPlan } from '@/routes/TrainingPlans/hooks/useIsAssignedToPlan';

const PLAN_DOCUMENT_ROUTE = '/plans/my-plans/:planId/doc/:stepId';

type TrainTogetherModeState = ExtractAtomValue<typeof trainTogetherModeAtom>;

const findPlanStep = (
  planData: UserPlans | undefined,
  stepId: string | undefined,
): ContentStep | undefined => {
  if (!planData?.plan?.content || !stepId) {
    return undefined;
  }

  const { content } = planData.plan;
  const topLevel = content.steps?.find(s => s.id === stepId);
  if (topLevel) {
    return topLevel;
  }

  for (const section of content.sections ?? []) {
    const found = section.steps?.find(s => s.id === stepId);
    if (found) {
      return found;
    }
  }

  return undefined;
};

const getTrainTogetherModeUserIds = (
  trainTogetherMode: boolean,
  planId: string | undefined,
  trainTogetherModeState: TrainTogetherModeState,
): string[] | undefined => {
  if (!trainTogetherMode) {
    return undefined;
  }

  const { trainTogetherModeData } = trainTogetherModeState;
  if (trainTogetherModeData.planId !== planId) {
    return undefined;
  }

  const ids = trainTogetherModeData.selectedTeamMemberIds.filter(
    (id): id is string => !!id,
  );

  return ids.length > 0 ? ids : undefined;
};

const getTrainTogetherStepStatus = (
  assignments: UserPlans['assignments'],
  userIds: string[],
  stepId: string,
) => {
  const relevant =
    assignments?.filter(
      a => a.userId !== undefined && userIds.includes(a.userId),
    ) ?? [];

  if (relevant.length === 0) {
    return Constants.TRAINING_PLANS.INCOMPLETE;
  }

  const allComplete = relevant.every(
    assignment =>
      assignment.steps?.find(s => s.stepId === stepId)?.status ===
      Constants.TRAINING_PLANS.COMPLETE,
  );

  return allComplete
    ? Constants.TRAINING_PLANS.COMPLETE
    : Constants.TRAINING_PLANS.INCOMPLETE;
};

const getSelfDirectedStepStatus = (
  assignments: UserPlans['assignments'],
  userId: string,
  stepId: string,
) => {
  const userAssignment = assignments?.find(a => a.userId === userId);
  const isComplete =
    userAssignment?.steps?.find(s => s.stepId === stepId)?.status ===
    Constants.TRAINING_PLANS.COMPLETE;

  return isComplete
    ? Constants.TRAINING_PLANS.COMPLETE
    : Constants.TRAINING_PLANS.INCOMPLETE;
};

const getStepStatus = (
  stepId: string | undefined,
  assignments: UserPlans['assignments'],
  trainTogetherMode: boolean,
  userIds: string[] | undefined,
  userId: string,
): string | undefined => {
  if (!stepId) {
    return undefined;
  }

  if (trainTogetherMode) {
    if (!userIds?.length) {
      return undefined;
    }

    return getTrainTogetherStepStatus(assignments, userIds, stepId);
  }

  if (userId) {
    return getSelfDirectedStepStatus(assignments, userId, stepId);
  }

  return Constants.TRAINING_PLANS.INCOMPLETE;
};

/** Composes URL, plan query, and Train Together session state for plan document routes. */
export const useDocProxyContext = () => {
  const isPlanDocumentRoute = !!useMatch(PLAN_DOCUMENT_ROUTE);
  const { planId, stepId } = useParams<{
    planId: string;
    stepId: string;
  }>();
  const [trainTogetherMode] = useQueryState(
    'trainTogetherMode',
    parseAsBoolean.withDefault(false),
  );
  const {
    data: auth,
    isSuccess: isAuthSuccess,
    isError: isAuthError,
    error: authError,
  } = useAuth();
  const userId = auth?.userId ?? '';
  const isWebAuthReady = window.native.isNative || isAuthSuccess;
  const isWaitingForWebAuth =
    !window.native.isNative && !isAuthSuccess && !isAuthError;
  const hasWebAuthError = !window.native.isNative && isAuthError;
  const trainTogetherModeState = useAtomValue(trainTogetherModeAtom);

  const safePlanId = planId ?? '';
  const isPlanIdValid = !!planId && isValidPlanId(planId);

  const userIds = getTrainTogetherModeUserIds(
    trainTogetherMode,
    planId,
    trainTogetherModeState,
  );
  const isTrainTogetherModeSessionValid =
    trainTogetherMode && !!userIds?.length;
  const isTrainTogetherModeSessionInvalid =
    trainTogetherMode && !userIds?.length;
  const skipAssignmentCheck = isTrainTogetherModeSessionValid;

  const { isAssigned, isAssignmentCheckError, assignmentCheckError } =
    useIsAssignedToPlan(safePlanId, {
      enabled:
        isPlanDocumentRoute &&
        isPlanIdValid &&
        isWebAuthReady &&
        !skipAssignmentCheck,
    });

  const hasClearedAssignmentCheck =
    skipAssignmentCheck || (isAssigned === true && !isAssignmentCheckError);

  const {
    data: planAndAssignments,
    isPending,
    isFetching,
    isError,
    error,
    isFetched,
  } = useQuery({
    ...getPlanAndAssignmentsOptions({ path: { planId: safePlanId } }),
    enabled:
      isPlanDocumentRoute &&
      isPlanIdValid &&
      isWebAuthReady &&
      hasClearedAssignmentCheck,
  });

  if (!isPlanDocumentRoute) {
    return {
      resourceReference: undefined,
      planName: '',
      planId: undefined,
      stepId: undefined,
      step: undefined,
      stepStatus: undefined,
      userIds: undefined,
      isTrainTogetherMode: false,
      isTrainTogetherModeSessionInvalid: false,
      isPlanIdValid: false,
      stepNotFound: false,
      isPending: false,
      isError: false,
      error: null,
      planAndAssignments: undefined,
      isPlanAccessDenied: false,
    };
  }

  const step = findPlanStep(planAndAssignments, stepId);
  const resourceReference = getPlanStepResourceReference(step);

  const isPlanAccessDenied =
    !skipAssignmentCheck && !isAssignmentCheckError && isAssigned === false;

  const isPlanContentLoading =
    (!skipAssignmentCheck &&
      isAssigned === undefined &&
      !isAssignmentCheckError) ||
    (hasClearedAssignmentCheck &&
      (isPending || (isFetching && !planAndAssignments?.plan)));

  return {
    resourceReference,
    planName: itemByLanguage(planAndAssignments?.plan?.name) ?? '',
    planId,
    stepId,
    step,
    stepStatus: planAndAssignments?.plan
      ? getStepStatus(
          stepId,
          planAndAssignments.assignments,
          trainTogetherMode,
          userIds,
          userId,
        )
      : undefined,
    userIds,
    isTrainTogetherMode: isTrainTogetherModeSessionValid,
    isTrainTogetherModeSessionInvalid,
    isPlanIdValid,
    stepNotFound: isFetched && !!planAndAssignments?.plan && !!stepId && !step,
    isPending: isWaitingForWebAuth || (isWebAuthReady && isPlanContentLoading),
    isError: hasWebAuthError || isAssignmentCheckError || isError,
    error: hasWebAuthError
      ? authError
      : isAssignmentCheckError
        ? assignmentCheckError
        : error,
    planAndAssignments,
    isPlanAccessDenied,
  };
};
